/**
 * Data API Client (Public read-only API)
 */
import { ApiError, NetworkError, NotFoundError } from './errors.js';
function buildActivityQuery(params) {
    const searchParams = new URLSearchParams();
    if (params?.days)
        searchParams.set('days', params.days.toString());
    if (params?.limit)
        searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return query ? `?${query}` : '';
}
export class DataClient {
    baseUrl;
    debug;
    authToken = null;
    authTokenProvider = null;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/$/, '');
        this.debug = options.debug ?? false;
    }
    /**
     * Set the Firebase auth token for authenticated requests
     */
    setAuthToken(token) {
        this.authToken = token;
    }
    /**
     * Get the current auth token
     */
    getAuthToken() {
        return this.authToken;
    }
    /**
     * Registers a callback that authRequest() calls to get a fresh token
     * immediately before every authenticated request, instead of trusting
     * whatever setAuthToken() last cached. Firebase ID tokens expire hourly;
     * a cached string alone goes stale if the tab is backgrounded/asleep
     * long enough that the caller's own onIdTokenChanged listener doesn't
     * get a chance to re-cache it in time - confirmed as the cause of
     * "Manage Account prompts to sign in" despite being genuinely signed
     * in. Callers typically wire this to Firebase's getIdToken(), which is
     * a no-op when the cached token is still valid and silently refreshes
     * it otherwise. Optional - callers that never wire this up (or pass
     * null) keep the old cached-token-only behavior.
     */
    setAuthTokenProvider(provider) {
        this.authTokenProvider = provider;
    }
    async request(path, options = {}) {
        const url = `${this.baseUrl}${path}`;
        if (this.debug) {
            console.log(`[DataClient] ${options.method ?? 'GET'} ${url}`);
        }
        try {
            const response = await fetch(url, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers,
                },
            });
            const data = (await response.json());
            if (!response.ok) {
                if (response.status === 404) {
                    throw new NotFoundError();
                }
                throw ApiError.fromResponse(response.status, data.error ?? {});
            }
            if (!data.success) {
                throw ApiError.fromResponse(response.status, data.error ?? {});
            }
            return data.data;
        }
        catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new NetworkError(error instanceof Error ? error.message : 'Network error');
        }
    }
    // Neighborhoods
    async getNeighborhoods(params) {
        const searchParams = new URLSearchParams();
        if (params?.page)
            searchParams.set('page', params.page.toString());
        if (params?.limit)
            searchParams.set('limit', params.limit.toString());
        if (params?.sortBy)
            searchParams.set('sortBy', params.sortBy);
        if (params?.sortOrder)
            searchParams.set('sortOrder', params.sortOrder);
        if (params?.summary)
            searchParams.set('fields', 'summary');
        if (params?.filters) {
            const { regionId, propertyType, priceRange, strAllowed, beachAccess, visible } = params.filters;
            if (regionId)
                searchParams.set('regionId', regionId);
            if (propertyType)
                searchParams.set('propertyType', propertyType);
            if (priceRange)
                searchParams.set('priceRange', priceRange);
            if (strAllowed !== undefined)
                searchParams.set('strAllowed', strAllowed.toString());
            if (beachAccess)
                searchParams.set('beachAccess', beachAccess);
            if (visible !== undefined)
                searchParams.set('visible', visible.toString());
        }
        const query = searchParams.toString();
        return this.request(`/neighborhoods${query ? `?${query}` : ''}`);
    }
    async getNeighborhood(slug) {
        return this.request(`/neighborhoods/${encodeURIComponent(slug)}`);
    }
    async getNeighborhoodListings(slug, params) {
        const searchParams = new URLSearchParams();
        if (params?.page)
            searchParams.set('page', params.page.toString());
        if (params?.limit)
            searchParams.set('limit', params.limit.toString());
        if (params?.status)
            searchParams.set('status', params.status);
        const query = searchParams.toString();
        return this.request(`/neighborhoods/${encodeURIComponent(slug)}/listings${query ? `?${query}` : ''}`);
    }
    /**
     * Get neighborhood sales data
     * @param slugOrId - Neighborhood slug or ID
     * @param params.chart - If true, includes full chart data (sales + listings)
     */
    async getNeighborhoodSales(slugOrId, params) {
        const searchParams = new URLSearchParams();
        if (params?.chart)
            searchParams.set('chart', 'true');
        const query = searchParams.toString();
        return this.request(`/neighborhoods/${encodeURIComponent(slugOrId)}/sales${query ? `?${query}` : ''}`);
    }
    // "Latest Activity" tab - new listings, price changes, pending, and sold.
    // Same response shape across neighborhoods/districts/regions (district and
    // region rows additionally carry neighborhoodId/neighborhoodName since
    // those scopes cascade across multiple neighborhoods).
    async getNeighborhoodActivity(slugOrId, params) {
        return this.request(`/neighborhoods/${encodeURIComponent(slugOrId)}/activity${buildActivityQuery(params)}`);
    }
    async getDistrictActivity(slug, params) {
        return this.request(`/districts/${encodeURIComponent(slug)}/activity${buildActivityQuery(params)}`);
    }
    async getRegionActivity(slug, params) {
        return this.request(`/regions/${encodeURIComponent(slug)}/activity${buildActivityQuery(params)}`);
    }
    // Listings
    async getListings(params) {
        const searchParams = new URLSearchParams();
        if (params?.page)
            searchParams.set('page', params.page.toString());
        if (params?.limit)
            searchParams.set('limit', params.limit.toString());
        if (params?.sortBy)
            searchParams.set('sortBy', params.sortBy);
        if (params?.sortOrder)
            searchParams.set('sortOrder', params.sortOrder);
        if (params?.filters) {
            const { neighborhoodId, status, minPrice, maxPrice, minBedrooms } = params.filters;
            if (neighborhoodId)
                searchParams.set('neighborhoodId', neighborhoodId);
            if (status) {
                const statuses = Array.isArray(status) ? status : [status];
                if (statuses[0])
                    searchParams.set('status', statuses[0]);
            }
            if (minPrice)
                searchParams.set('minPrice', minPrice.toString());
            if (maxPrice)
                searchParams.set('maxPrice', maxPrice.toString());
            if (minBedrooms)
                searchParams.set('minBedrooms', minBedrooms.toString());
        }
        const query = searchParams.toString();
        return this.request(`/listings${query ? `?${query}` : ''}`);
    }
    async getListing(mlsId) {
        return this.request(`/listings/${encodeURIComponent(mlsId)}`);
    }
    /**
     * Bulk fetch listings by internal id - used to hydrate a specific,
     * pre-determined set of listings (e.g. Ask search results) into full
     * ListingSummary objects for the map/sidebar.
     */
    async getListingsByIds(ids) {
        const searchParams = new URLSearchParams({ ids: ids.join(',') });
        return this.request(`/listings/by-ids?${searchParams.toString()}`);
    }
    // Bulk fetch TMK-only parcels (never an active MLS listing) by their
    // normalized TMK - see migration 069's item_type='tmk' (Cart/Story
    // items for a property with no mls_listings row at all).
    async getTmksByIds(tmks) {
        const searchParams = new URLSearchParams({ tmks: tmks.join(',') });
        return this.request(`/tmk/by-ids?${searchParams.toString()}`);
    }
    async getMapMarkers(params) {
        const searchParams = new URLSearchParams();
        if (params?.status)
            searchParams.set('status', params.status);
        if (params?.minPrice)
            searchParams.set('minPrice', params.minPrice.toString());
        if (params?.maxPrice)
            searchParams.set('maxPrice', params.maxPrice.toString());
        if (params?.minBedrooms)
            searchParams.set('minBedrooms', params.minBedrooms.toString());
        if (params?.propertyType)
            searchParams.set('propertyType', params.propertyType);
        if (params?.neighborhoodId)
            searchParams.set('neighborhoodId', params.neighborhoodId);
        if (params?.districtId)
            searchParams.set('districtId', params.districtId);
        if (params?.regionId)
            searchParams.set('regionId', params.regionId);
        if (params?.bounds) {
            searchParams.set('north', params.bounds.north.toString());
            searchParams.set('south', params.bounds.south.toString());
            searchParams.set('east', params.bounds.east.toString());
            searchParams.set('west', params.bounds.west.toString());
        }
        const query = searchParams.toString();
        const result = await this.request(`/listings/map/markers${query ? `?${query}` : ''}`);
        return result.listings;
    }
    /**
     * Get markers from tmk_status table (new unified endpoint)
     * For both map bounds view and NHM (Neighborhood Mode)
     * Returns pre-computed status and badges
     */
    async getMarkers(params) {
        const searchParams = new URLSearchParams();
        if (params.status)
            searchParams.set('status', params.status);
        if (params.neighborhoodId)
            searchParams.set('neighborhoodId', params.neighborhoodId);
        if (params.tmk)
            searchParams.set('tmk', params.tmk);
        if (params.bounds) {
            searchParams.set('north', params.bounds.north.toString());
            searchParams.set('south', params.bounds.south.toString());
            searchParams.set('east', params.bounds.east.toString());
            searchParams.set('west', params.bounds.west.toString());
        }
        if (params.minPrice)
            searchParams.set('minPrice', params.minPrice.toString());
        if (params.maxPrice)
            searchParams.set('maxPrice', params.maxPrice.toString());
        if (params.minBedrooms)
            searchParams.set('minBedrooms', params.minBedrooms.toString());
        if (params.propertyType)
            searchParams.set('propertyType', params.propertyType);
        const query = searchParams.toString();
        return this.request(`/listings/markers${query ? `?${query}` : ''}`);
    }
    /**
     * Keyword filter (FilterDrawer's Keyword field) - plain multi-term AND
     * LIKE over listing remarks, not the AI-parsed /ask/* search. Returns
     * tmkNormalized values, the one identity every ListingSummary source
     * agrees on (see listingItemKey()).
     */
    async searchListingIds(q, status) {
        const searchParams = new URLSearchParams({ q });
        if (status)
            searchParams.set('status', status);
        const result = await this.request(`/listings/search-ids?${searchParams.toString()}`);
        return result.tmks;
    }
    // Regions
    async getRegions() {
        return this.request('/regions');
    }
    async getRegion(slug) {
        return this.request(`/regions/${encodeURIComponent(slug)}`);
    }
    async getRegionNeighborhoods(slug) {
        return this.request(`/regions/${encodeURIComponent(slug)}/neighborhoods`);
    }
    /**
     * Get sales/for-sale rollup data across every neighborhood in a region -
     * same shape as getNeighborhoodSales(), for the region-level GeographyModal
     * popup. See GeographySalesData above.
     * @param params.chart - If true, includes full chart data (sales + listings)
     */
    async getRegionSales(slug, params) {
        const searchParams = new URLSearchParams();
        if (params?.chart)
            searchParams.set('chart', 'true');
        const query = searchParams.toString();
        return this.request(`/regions/${encodeURIComponent(slug)}/sales${query ? `?${query}` : ''}`);
    }
    /**
     * Get monthly market-temperature indicators (median DOM, months of
     * supply, sale-to-list ratio, % price drops, new listings vs. sold) for
     * the region-level GeographyModal "Market Data" tab. See
     * GeographyMarketTrendsData above.
     * @param params.months - How many months of history to return (default 24)
     */
    async getRegionMarketTrends(slug, params) {
        const searchParams = new URLSearchParams();
        if (params?.months)
            searchParams.set('months', String(params.months));
        const query = searchParams.toString();
        return this.request(`/regions/${encodeURIComponent(slug)}/market-trends${query ? `?${query}` : ''}`);
    }
    // Counties
    async getCounties() {
        return this.request('/counties');
    }
    async getCounty(id) {
        return this.request(`/counties/${encodeURIComponent(id)}`);
    }
    async getCountySales(id, params) {
        const searchParams = new URLSearchParams();
        if (params?.chart)
            searchParams.set('chart', 'true');
        const query = searchParams.toString();
        return this.request(`/counties/${encodeURIComponent(id)}/sales${query ? `?${query}` : ''}`);
    }
    async getCountyMarketTrends(id, params) {
        const searchParams = new URLSearchParams();
        if (params?.months)
            searchParams.set('months', String(params.months));
        const query = searchParams.toString();
        return this.request(`/counties/${encodeURIComponent(id)}/market-trends${query ? `?${query}` : ''}`);
    }
    async getCountyActivity(id, params) {
        return this.request(`/counties/${encodeURIComponent(id)}/activity${buildActivityQuery(params)}`);
    }
    // Districts
    async getDistricts() {
        return this.request(`/districts`);
    }
    async getDistrict(slug) {
        return this.request(`/districts/${encodeURIComponent(slug)}`);
    }
    /**
     * Get sales/for-sale rollup data across every neighborhood in a district -
     * same shape as getNeighborhoodSales(), for the district-level
     * GeographyModal popup. See GeographySalesData above.
     * @param params.chart - If true, includes full chart data (sales + listings)
     */
    async getDistrictSales(slug, params) {
        const searchParams = new URLSearchParams();
        if (params?.chart)
            searchParams.set('chart', 'true');
        const query = searchParams.toString();
        return this.request(`/districts/${encodeURIComponent(slug)}/sales${query ? `?${query}` : ''}`);
    }
    /**
     * Get monthly market-temperature indicators for the district-level
     * GeographyModal "Market Data" tab. See getRegionMarketTrends above.
     * @param params.months - How many months of history to return (default 24)
     */
    async getDistrictMarketTrends(slug, params) {
        const searchParams = new URLSearchParams();
        if (params?.months)
            searchParams.set('months', String(params.months));
        const query = searchParams.toString();
        return this.request(`/districts/${encodeURIComponent(slug)}/market-trends${query ? `?${query}` : ''}`);
    }
    // Stats
    /**
     * Two selectable metrics - median closed-sale price (trailing 12 months)
     * and median current list price (Active listings, no time window) - by
     * neighborhood and by district, for the Display tab's "Market Trends" map
     * fill.
     */
    async getPriceHeatmap() {
        return this.request(`/stats/price-heatmap`);
    }
    /**
     * Current-snapshot market stats (active count, median price/$psf,
     * absorption, sale-to-list) for the given district(s), plus the prior-
     * period values for each so the caller can show a delta - same endpoint
     * MarketStats' Overview tab uses (packages/ui-components/src/MarketStats),
     * now also used by GeographyModal's Market Data tab headline strip scoped
     * to a single region/district. Only the `headline` field is typed here
     * (the only part either caller reads); the real response also carries
     * absorption/saleToList/strPremium/priceDistribution breakdowns.
     */
    async getStatsOverview(params) {
        const query = new URLSearchParams();
        if (params.districts && params.districts.length > 0)
            query.set('districts', params.districts.join(','));
        query.set('window', params.window || '90d');
        return this.request(`/stats/overview?${query.toString()}`);
    }
    /**
     * Resolves a sent Buyer/Seller Message (agent_stories, share_token) to
     * its full listing/neighborhood data - powers the "Agent Picks" attribute
     * (apps/public's utils/agentPicks.ts) after someone opens a `?story=`
     * link. Unauthenticated, only ever resolves already-SENT stories (see
     * workers/data-api's routes/stories.ts).
     */
    async getStory(token) {
        return this.request(`/stories/${encodeURIComponent(token)}`);
    }
    /**
     * An agent's currently published Featured list (persistent, keyed by
     * agent slug, never expires) - powers the "Featured" attribute (apps/
     * public's utils/agentFeatured.ts) after someone opens a `?agent=`
     * link. Distinct from getStory() above: not gated on a one-off token,
     * and an agent with nothing published simply returns empty arrays
     * rather than a 404 (see workers/data-api's routes/agentFeatured.ts).
     */
    async getAgentFeatured(slug) {
        return this.request(`/agents/${encodeURIComponent(slug)}/featured`);
    }
    /**
     * Minimal public profile lookup (name only) for whichever agent actually
     * owns the current subdomain - used to personalize "Contact <First
     * Name>" across the header, Contact modal, and PropertyModal. Distinct
     * from getAgentFeatured() above: that's keyed by an ad-hoc `?agent=`
     * share link, this is "who runs this site." Throws NotFoundError if the
     * subdomain has no approved agent - callers should fail open to generic
     * copy rather than block on it.
     */
    async getAgentProfile(slug) {
        return this.request(`/agents/${encodeURIComponent(slug)}`);
    }
    /**
     * Published custom pages (migration 063 / agent_pages) for whichever
     * agent owns the current subdomain - the same endpoint
     * workers/agent-site/src/agent-nav-runtime.js already polls (plain JS,
     * can't use this client) to keep a published page's nav fresh without
     * republishing. apps/public's persistent header is now a third consumer
     * of this same endpoint (see workers/data-api/src/routes/agentPages.ts).
     */
    async getAgentPages(slug) {
        return this.request(`/agents/${encodeURIComponent(slug)}/pages`);
    }
    /**
     * One representative value per neighborhood/district for the Display
     * drawer's Climate overlay - rainfall is a trailing-12-month total,
     * temperature a trailing-12-month average (see workers/data-api's
     * /climate route for why). Includes the range for building a legend and
     * the data_sources row it came from, for on-map attribution.
     */
    async getClimateData(params) {
        const query = new URLSearchParams({ entityType: params.entityType, datatype: params.datatype });
        return this.request(`/climate?${query.toString()}`);
    }
    // Search
    async search(params) {
        const searchParams = new URLSearchParams();
        searchParams.set('q', params.q);
        if (params.type)
            searchParams.set('type', params.type);
        if (params.page)
            searchParams.set('page', params.page.toString());
        if (params.limit)
            searchParams.set('limit', params.limit.toString());
        return this.request(`/search?${searchParams.toString()}`);
    }
    async searchSuggest(params) {
        const searchParams = new URLSearchParams();
        searchParams.set('q', params.q);
        if (params.limit)
            searchParams.set('limit', params.limit.toString());
        return this.request(`/search/suggest?${searchParams.toString()}`);
    }
    // Ask
    /**
     * Natural-language search - parses the query, then runs one combined
     * retrieval (structured filter + freshness + semantic search) and
     * returns real listings plus a grounded prose summary. See
     * workers/data-api/src/routes/ask.ts's POST /ask/search for the full
     * mechanics; this is a thin wrapper, same fetch/error-handling shape as
     * every other method here via the shared `request()` helper.
     */
    async askSearch(query) {
        return this.request('/ask/search', {
            method: 'POST',
            body: JSON.stringify({ query }),
        });
    }
    // Sales
    /**
     * Get sales history for a specific TMK
     */
    async getSalesByTmk(tmkId) {
        const response = await this.request(`/sales?tmkId=${encodeURIComponent(tmkId)}&limit=20`);
        return response.data;
    }
    // =============================================================================
    // Authenticated User Endpoints
    // =============================================================================
    /**
     * Make an authenticated request (requires auth token to be set)
     */
    async authRequest(path, options = {}) {
        const token = this.authTokenProvider ? await this.authTokenProvider() : this.authToken;
        if (!token) {
            throw new ApiError('UNAUTHORIZED', 'Authentication required', 401);
        }
        return this.request(path, {
            ...options,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${token}`,
            },
        });
    }
    /**
     * Get current user profile
     * Returns null if user is not registered (404)
     */
    async getCurrentUser() {
        try {
            return await this.authRequest('/users/me');
        }
        catch (error) {
            if (error instanceof NotFoundError) {
                return null;
            }
            throw error;
        }
    }
    /**
     * Create a new user (first login)
     * @param params.subdomain - The subdomain the user signed up from
     * @param params.favorites - Optional localStorage favorites to sync
     * @param params.alerts - Optional localStorage alerts to sync
     */
    async createUser(params) {
        return this.authRequest('/users', {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    // Favorites
    /**
     * Get user's favorites
     */
    async getFavorites() {
        return this.authRequest('/users/me/favorites');
    }
    /**
     * Add a favorite
     */
    async addFavorite(itemId, itemType) {
        await this.authRequest('/users/me/favorites', {
            method: 'POST',
            body: JSON.stringify({ itemId, itemType }),
        });
    }
    /**
     * Remove a favorite
     */
    async removeFavorite(itemId) {
        await this.authRequest(`/users/me/favorites/${encodeURIComponent(itemId)}`, {
            method: 'DELETE',
        });
    }
    // Alerts
    /**
     * Get user's alerts
     */
    async getAlerts() {
        return this.authRequest('/users/me/alerts');
    }
    /**
     * Add an alert
     */
    async addAlert(itemId, itemType) {
        await this.authRequest('/users/me/alerts', {
            method: 'POST',
            body: JSON.stringify({ itemId, itemType }),
        });
    }
    /**
     * Remove an alert
     */
    async removeAlert(itemId) {
        await this.authRequest(`/users/me/alerts/${encodeURIComponent(itemId)}`, {
            method: 'DELETE',
        });
    }
    /**
     * Pause an alert (still owned, but skipped by the notifications cron).
     */
    async pauseAlert(itemId) {
        await this.authRequest(`/users/me/alerts/${encodeURIComponent(itemId)}/pause`, {
            method: 'POST',
        });
    }
    /**
     * Resume a paused alert.
     */
    async resumeAlert(itemId) {
        await this.authRequest(`/users/me/alerts/${encodeURIComponent(itemId)}/resume`, {
            method: 'POST',
        });
    }
    /**
     * Combined Account-page payload: profile + favorites + alerts with names.
     */
    async getAccount() {
        return this.authRequest('/users/me/account');
    }
    /**
     * U-38 — delete the user's D1 footprint (favorites, alerts, contact rows,
     * users row). Caller should follow up by deleting the Firebase Auth account
     * and signing the user out.
     */
    async deleteAccount() {
        await this.authRequest('/users/me', { method: 'DELETE' });
    }
}
//# sourceMappingURL=data.js.map